import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Modal, Form, Input, Select, Tag, Popconfirm, message, Space, Typography, Spin } from 'antd';
import { UserAddOutlined, UserDeleteOutlined, TeamOutlined, MailOutlined, SafetyOutlined } from '@ant-design/icons';
import apiClient from '../api';

const { Title, Paragraph } = Typography;

const UserManager = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalVisible, setModalVisible] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form] = Form.useForm();

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    setLoading(true);
    try {
      const res = await apiClient.get('/accounts/users');
      if (res.data.success) {
        setUsers(res.data.users);
      }
    } catch (error) {
      message.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleAddUser = async (values) => {
    setSubmitting(true);
    try {
      const res = await apiClient.post('/accounts/users', values);
      if (res.data.success) {
        message.success('User added successfully');
        setModalVisible(false);
        form.resetFields();
        loadUsers();
      }
    } catch (error) {
      message.error(error.response?.data?.message || 'Failed to add user');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteUser = async (userId) => {
    try {
      const res = await apiClient.delete(`/accounts/users/${userId}`);
      if (res.data.success) {
        message.success('User removed');
        loadUsers();
      }
    } catch (error) {
      message.error('Failed to remove user');
    }
  };

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (text) => <b>{text}</b>,
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      render: (text) => <Space><MailOutlined /> {text}</Space>,
    },
    {
      title: 'Role',
      dataIndex: 'role',
      key: 'role',
      render: (role) => {
        let color = 'blue';
        if (role === 'admin') color = 'gold';
        if (role === 'editor') color = 'cyan';
        return <Tag color={color}>{role.toUpperCase()}</Tag>;
      },
    },
    {
      title: 'Added',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date) => new Date(date).toLocaleDateString(),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Popconfirm
          title="Remove this user?"
          description="They will no longer have access to this account."
          onConfirm={() => handleDeleteUser(record._id)}
          okText="Yes, Remove"
          cancelText="No"
          okButtonProps={{ danger: true }}
        >
          <Button type="text" danger icon={<UserDeleteOutlined />} size="small">
            Remove
          </Button>
        </Popconfirm>
      ),
    },
  ];

  return (
    <div style={{ maxWidth: 900, margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <Title level={2}><TeamOutlined /> User Management</Title>
          <Paragraph type="secondary">Manage team members and their roles within this account.</Paragraph>
        </div>
        <Button 
          type="primary" 
          icon={<UserAddOutlined />} 
          onClick={() => setModalVisible(true)}
        >
          Add Team Member
        </Button>
      </div>

      <Card>
        <Table 
          columns={columns} 
          dataSource={users} 
          rowKey="_id" 
          loading={loading}
          pagination={false}
        />
      </Card>

      <Modal
        title="Invite Team Member"
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
      >
        <Form form={form} layout="vertical" onFinish={handleAddUser}>
          <Form.Item name="name" label="Full Name" rules={[{ required: true }]}>
            <Input placeholder="John Doe" />
          </Form.Item>
          <Form.Item name="email" label="Email Address" rules={[{ required: true, type: 'email' }]}>
            <Input prefix={<MailOutlined />} placeholder="john@example.com" />
          </Form.Item>
          <Form.Item name="role" label="Role" initialValue="viewer">
            <Select>
              <Select.Option value="admin">Admin (Full Access)</Select.Option>
              <Select.Option value="editor">Editor (Can edit agents/templates)</Select.Option>
              <Select.Option value="viewer">Viewer (Read-only logs/history)</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item name="password" label="Temporary Password" rules={[{ required: true, min: 6 }]}>
            <Input.Password prefix={<SafetyOutlined />} placeholder="Min 6 characters" />
          </Form.Item>
          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={() => setModalVisible(false)}>Cancel</Button>
              <Button type="primary" htmlType="submit" loading={submitting}>
                Add User
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default UserManager;
